import React, {useMemo, useState} from 'react';
import {motion} from 'framer-motion';
import {ContactCTA, Footer, Navbar, ProjectCard} from '../../components';
import {getCategories, getStates, PROJECTS} from '../../data/projects';
import styles from './Projects.module.css';

const GRID_ICON = "https://res.cloudinary.com/dhjzovpof/image/upload/v1777649350/Vector_zguioe.png";
const LIST_ICON = "https://res.cloudinary.com/dhjzovpof/image/upload/v1777649393/icon-park-outline_list_jpbtxz.png";
const ALL = 'All';

export default function Projects() {
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState(ALL);
    const [activeState, setActiveState] = useState(ALL);
    const [sortBy, setSortBy] = useState(''); // 'date' | 'alpha'
    const [filterOpen, setFilterOpen] = useState(false);
    const [isAscending, setIsAscending] = useState(false);
    const [isListView, setIsListView] = useState(false);
    const categories = [ALL, ...getCategories()];
    const states = [ALL, ...getStates()];

    const filtered = useMemo(() => {
        let list = PROJECTS.filter((p) => {
            const q = search.toLowerCase();
            const matchQ = !q || p.title.toLowerCase().includes(q) || p.location.toLowerCase().includes(q);
            const matchCat = activeCategory === ALL || p.category === activeCategory;
            const matchSt = activeState === ALL || p.state === activeState;
            return matchQ && matchCat && matchSt;
        });
        if (sortBy === 'alpha') {
            if (isAscending) {
                list = [...list].sort((a, b) => a.title.localeCompare(b.title));
            } else {
                list = [...list].sort((a, b) => b.title.localeCompare(a.title));
            }
        }
        if (sortBy === 'date') {
            if (isAscending) {
                list = [...list].sort((a, b) => a.year - b.year);
            } else {
                list = [...list].sort((a, b) => b.year - a.year);
            }
        }
        return list;
    }, [search, activeCategory, activeState, sortBy, isAscending]);

    return (
        <div className={styles.page}>
            <Navbar theme="light" cta="Contact Us" ctaTo="/contact"/>

            {/* ── TITLE BAR ─────────────────────────────────────── */}
            <div className={styles.titleBar}>
                <span className={styles.titleBarLeft}>Projects</span>
                <div className={styles.rightBar}>
                    <span className={styles.titleBarRight}><span style={{color: '#CF181F'}}>{filtered.length}</span> Projects</span>
                    <span onClick={() => setIsListView(prev => !prev)}
                          className={styles.titleBarRightList}>
                        {isListView
                            ? <img src={GRID_ICON} className={styles.icon} alt="Grid view" />
                            : <img src={LIST_ICON}  className={styles.icon} alt="List view" />
                        }
                        <span className={styles.viewLabel}>
                            {!isListView ? 'View List' : 'View Grid'}
                        </span>
                    </span>
                </div>
            </div>

            {/* ── FILTER BAR ────────────────────────────────────── */}
            <div className={`${styles.filterBar} ${filterOpen ? styles.filterBarOpen : ''}`}>
                {/* Row 1: Search + Filter button */}
                <div className={styles.filterRow1}>
                    <div className={styles.searchPill}>
                        <input
                            className={styles.searchInput}
                            type="text"
                            placeholder="Search Project"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search projects"
                        />
                    </div>
                    <button
                        className={styles.filterToggleBtn}
                        onClick={() => {
                            setFilterOpen(v => !v)
                            setIsAscending(v => !v)
                        }}
                        aria-expanded={filterOpen}
                        aria-label="Toggle filters"
                    >
                        <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M13.9911 0H0.213347C0.155184 0.00448708 0.10087 0.0308033 0.0613026 0.0736684C0.0217348 0.116533 -0.000159094 0.172776 1.37323e-05 0.231111V0.995556C-0.000520337 1.07391 0.0145286 1.15159 0.0442855 1.22407C0.0740424 1.29656 0.117913 1.3624 0.173347 1.41778L5.50668 6.75111V11.1956L8.7289 12.8V6.74222L14.0622 1.40889C14.1643 1.29883 14.2214 1.15453 14.2222 1.00444V0.231111C14.2222 0.169817 14.1979 0.111033 14.1545 0.067691C14.1112 0.0243492 14.0524 0 13.9911 0Z"
                                fill="#5E5E5E"/>
                        </svg>

                        <span>Filter</span>
                    </button>
                </div>

                {/* Row 2+: Dropdowns + sort (desktop always visible; mobile toggle) */}
                <div className={styles.filterExpanded}>
                    {/* All Projects dropdown */}
                    <div className={styles.selectPill}>
                        <select
                            className={styles.selectInner}
                            value={activeCategory}
                            onChange={(e) => setActiveCategory(e.target.value)}
                            aria-label="Filter by category"
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>{c === ALL ? 'All Projects' : c}</option>
                            ))}
                        </select>
                        <svg className={styles.chevron} width="14" height="14" viewBox="0 0 14 14" fill="none"
                             aria-hidden="true">
                            <path d="M2 4.5l5 5 5-5" stroke="#111" strokeWidth="1.5" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                    </div>

                    {/* Location dropdown */}
                    <div className={styles.selectPill}>
                        <select
                            className={styles.selectInner}
                            value={activeState}
                            onChange={(e) => setActiveState(e.target.value)}
                            aria-label="Filter by location"
                        >
                            {states.map((s) => (
                                <option key={s} value={s}>{s === ALL ? 'All Location' : s}</option>
                            ))}
                        </select>
                        <svg className={styles.chevron} width="14" height="14" viewBox="0 0 14 14" fill="none"
                             aria-hidden="true">
                            <path d="M2 4.5l5 5 5-5" stroke="#111" strokeWidth="1.5" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                    </div>

                    {/* Sort toggles */}
                    <div className={styles.sortGroup}>
                        <button
                            className={[styles.sortBtn, sortBy === 'date' ? styles.sortActive : styles.sortInactive].join(' ')}
                            onClick={() => {
                                console.log({sortBy, isAscending});
                                if (sortBy === 'date') {
                                    setIsAscending(v => !v);
                                } else {
                                    setSortBy('date');
                                    setIsAscending(v => !v);
                                }
                            }}
                        >
                            Date
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                <path d="M7 11V3M4 6l3-3 3 3" stroke="currentColor" strokeWidth="1.4"
                                      strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <button
                            className={[styles.sortBtn, sortBy === 'alpha' ? styles.sortActive : styles.sortInactive].join(' ')}
                            onClick={() => {
                                if (sortBy === 'alpha') {
                                    setIsAscending(v => !v);
                                } else {
                                    setSortBy('alpha');
                                    setIsAscending(v => !v);
                                }
                            }}
                        >
                            Alphabetical
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                <path d="M7 11V3M4 6l3-3 3 3" stroke="currentColor" strokeWidth="1.4"
                                      strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* ── PROJECTS GRID / LIST ──────────────────────────── */}
            <section className={styles.gridSection}>
                {filtered.length === 0 ? (
                    <motion.p
                        className={styles.empty}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                    >
                        No projects match your filters.
                    </motion.p>
                ) : isListView ? (
                    <div className={styles.tableWrapper}>
                        <table className={styles.listTable}>
                            <thead>
                            <tr>
                                <th className={`${styles.th} ${styles.thSN}`}>S/N</th>
                                <th className={styles.th}>Service</th>
                                <th className={styles.th}>Client</th>
                                <th className={styles.th}>Description</th>
                                <th className={styles.th}>Year</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filtered.map((project, i) => (
                                <tr key={project.id} className={styles.tr}>
                                    <td className={styles.tdSN}>{i + 1}</td>
                                    <td className={styles.td}>{(project.listService || '').toUpperCase()}</td>
                                    <td className={styles.td}>{project.client.toUpperCase()}</td>
                                    <td className={styles.td}>{project.type.toUpperCase()}</td>
                                    <td className={styles.td}>{project.year}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={styles.grid}>
                        {/* eslint-disable-next-line array-callback-return */}
                        {filtered.map((project, i) => {
                                if (!project.showInListOnly) {
                                    return <ProjectCard
                                        key={project.id}
                                        project={project}
                                        delay={(i % 3) * 0.05}
                                    />
                                }
                            }
                        )}
                    </div>
                )
                }
            </section>

            <ContactCTA/>
            <Footer/>
        </div>
    )
        ;
}
